import { useCallback } from "react";
import { useAtomValue, useSetAtom, useStore } from "jotai";
import type { PrimitiveAtom, SetStateAction } from "jotai";
import { sessionEpochAtom } from "@/store/sessionAtoms";

// Whether the session this hook was rendered in is still the current one. The
// one session test shared by useSessionSetAtom and useApi's toasts. A new
// session re-renders every consumer, so a function created in the next session
// gets a check that passes.
export const useIsSessionCurrent = () => {
    const store = useStore();
    const epoch = useAtomValue(sessionEpochAtom);

    return useCallback(
        () => store.get(sessionEpochAtom) === epoch,
        [store, epoch],
    );
};

// useSetAtom for an atom that endSessionAtom clears: the setter belongs to the
// session it was rendered in, and a write made after that session ended is
// dropped. An api hook's read captures this setter when it starts, so its
// answer landing after sign-out cannot repopulate what the reset just cleared.
// A new session re-renders every consumer with a fresh setter, so the next
// session's reads publish normally.
//
// Only for data an account read produces. In-flight flags (isFetching…,
// isProcessing…) keep the plain setter: endSessionAtom leaves them to settle
// in their own `finally`, and a dropped `false` would leave them stuck.
export const useSessionSetAtom = <Value>(anAtom: PrimitiveAtom<Value>) => {
    const isSessionCurrent = useIsSessionCurrent();
    const set = useSetAtom(anAtom);

    return useCallback(
        (update: SetStateAction<Value>) => {
            if (isSessionCurrent()) set(update);
        },
        [isSessionCurrent, set],
    );
};
